import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import RedeemCodeForm from './components/RedeemCodeForm';
import RedeemCodeResult from './components/RedeemCodeResult';
import CustomerInfoPage from './components/CustomerInfoPage';
import MaintenancePage from './components/MaintenancePage';
import CheckCodePage from './components/CheckCodePage';
import CheckCodeResult from './components/CheckCodeResult';
import API from './services/API';

// AuthWrapper component để kiểm tra authentication
const AuthWrapper = ({ children, onLogout }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [accessToken, setAccessToken] = useState(null);

  const fetchUserPoints = useCallback(async (accessToken) => {
    try {
      console.log('🚀 AuthWrapper fetchUserPoints - Starting points fetch with access token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');
      const pointsResult = await API.getCampaignPointsDetail(accessToken);
      console.log('🔍 AuthWrapper fetchUserPoints - API response:', pointsResult);

      if (pointsResult.success) {
        setTotalPoints(pointsResult.totalPoints);
        console.log('✅ AuthWrapper fetchUserPoints - User points loaded successfully:', pointsResult.totalPoints);
      } else {
        console.warn('⚠️ AuthWrapper fetchUserPoints - API call succeeded but no success flag:', pointsResult);
        setTotalPoints(0);
      }
    } catch (error) {
      console.error('❌ AuthWrapper fetchUserPoints - Error fetching user points:', error);
      // Don't block the UI if points fetch fails
      setTotalPoints(0);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    console.log('🔍 AuthWrapper checkAuth - Starting auth check...', { token: token ? token.substring(0, 20) + '...' : 'None' });

    if (token) {
      // Simple token verification without device fingerprint
      try {
        const decodedToken = JSON.parse(atob(token.replace(/[^a-zA-Z0-9]/g, '')));
        const phoneNumber = decodedToken.phone;
        const timestamp = decodedToken.timestamp;

        // Check if token is not too old (24 hours)
        const tokenAge = Date.now() - timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours

        if (tokenAge > maxAge) {
          console.log('❌ AuthWrapper checkAuth - Token expired');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        console.log('✅ AuthWrapper checkAuth - Token is valid, setting authenticated');
        setIsAuthenticated(true);

        // Get full user information from API
        try {
          const userCheck = await API.checkUserExists(phoneNumber);
          console.log('🔍 AuthWrapper checkAuth - User check result:', userCheck);

          const userInfoData = {
            phoneNumber: phoneNumber,
            token: token,
            sessionId: decodedToken.sessionId,
            timestamp: timestamp,
            ...userCheck.userInfo // Include all user info from API
          };
          setUserInfo(userInfoData);
        } catch (error) {
          console.error('❌ AuthWrapper checkAuth - Error fetching user info:', error);
          // Fallback to basic user info
          const userInfoData = {
            phoneNumber: phoneNumber,
            token: token,
            sessionId: decodedToken.sessionId,
            timestamp: timestamp
          };
          setUserInfo(userInfoData);
        }

        // Try to get access token from login response
        try {
          // Function to retry getting stored access token (for timing issues)
          const getStoredAccessTokenWithRetry = async (maxRetries = 5) => {
            for (let i = 0; i < maxRetries; i++) {
              const storedAccessToken = sessionStorage.getItem('accessToken');
              if (storedAccessToken) {
                console.log(`✅ AuthWrapper checkAuth - Found stored access token on attempt ${i + 1}`);
                return storedAccessToken;
              }

              if (i < maxRetries - 1) {
                console.log(`🔄 AuthWrapper checkAuth - Retry ${i + 1}/${maxRetries} for stored access token...`);
                await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
              }
            }
            console.log('❌ AuthWrapper checkAuth - No stored access token found after all retries');
            return null;
          };

          // Check if there's stored access token from login (with retry for timing)
          const storedAccessToken = await getStoredAccessTokenWithRetry();
          console.log('🔍 AuthWrapper checkAuth - Stored access token:', storedAccessToken ? storedAccessToken.substring(0, 20) + '...' : 'None');

          if (storedAccessToken) {
            console.log('✅ AuthWrapper checkAuth - Using stored access token for points API');
            setAccessToken(storedAccessToken);
            await fetchUserPoints(storedAccessToken);
          } else {
            // Try to get access token by calling login API again
            console.log('⚠️ AuthWrapper checkAuth - No stored access token, attempting to get access token...');
            const userCheck = await API.checkUserExists(phoneNumber);
            console.log('🔍 AuthWrapper checkAuth - API checkUserExists result:', {
              hasAccessToken: !!userCheck.accessToken,
              accessToken: userCheck.accessToken ? userCheck.accessToken.substring(0, 20) + '...' : 'None'
            });

            if (userCheck.accessToken) {
              console.log('✅ AuthWrapper checkAuth - Got access token from API, storing and fetching points');
              setAccessToken(userCheck.accessToken);
              sessionStorage.setItem('accessToken', userCheck.accessToken);
              await fetchUserPoints(userCheck.accessToken);
            } else {
              console.warn('❌ AuthWrapper checkAuth - No access token available for points API');
            }
          }
        } catch (error) {
          console.error('❌ AuthWrapper checkAuth - Error fetching access token or points:', error);
        }
      } catch (tokenError) {
        console.log('❌ AuthWrapper checkAuth - Token verification failed:', tokenError);
        setIsAuthenticated(false);
      }
    } else {
      console.log('❌ AuthWrapper checkAuth - No token found in URL');
      setIsAuthenticated(false);
    }

    setIsLoading(false);
  }, [fetchUserPoints]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Watch for URL changes and re-check auth if token changes
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const currentToken = urlParams.get('token');
      const currentUserToken = userInfo?.token;

      // If we have a new token that's different from current user token, re-check auth
      if (currentToken && currentToken !== currentUserToken) {
        console.log('🔄 AuthWrapper - URL token changed, re-checking auth...');
        checkAuth();
      }
    };

    // Listen for popstate events (back/forward)
    window.addEventListener('popstate', handleUrlChange);

    // Also check on location change via history API
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function () {
      originalPushState.apply(this, arguments);
      setTimeout(handleUrlChange, 0);
    };

    window.history.replaceState = function () {
      originalReplaceState.apply(this, arguments);
      setTimeout(handleUrlChange, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [userInfo?.token, checkAuth]);

  // Manual refresh points function (for debugging)
  const handleRefreshPoints = async () => {
    if (accessToken) {
      console.log('🔄 AuthWrapper - Manual points refresh triggered');
      await fetchUserPoints(accessToken);
    } else {
      console.warn('⚠️ AuthWrapper - Cannot refresh points: no access token available');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserInfo(null);
    setTotalPoints(0);
    setAccessToken(null);
    // Clear stored access token
    sessionStorage.removeItem('accessToken');
    // Redirect to maintenance page
    window.location.href = '/';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang kiểm tra xác thực...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect về trang maintenance thay vì login
    return <Navigate to="/" replace />;
  }

  // Clone children và truyền props cần thiết
  return React.cloneElement(children, {
    userInfo,
    onLogout: handleLogout,
    token: userInfo?.token,
    totalPoints: totalPoints,
    accessToken: accessToken,
    onRefreshPoints: handleRefreshPoints
  });
};

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Route mặc định - hiển thị trang kiểm tra sản phẩm */}
          <Route path="/" element={<CheckCodePage />} />

          {/* Route cho kết quả kiểm tra sản phẩm */}
          <Route path="/check-result" element={<CheckCodeResult />} />

          {/* Route cho redeem code form - yêu cầu đăng nhập */}
          <Route
            path="/redeem-code-form"
            element={
              <AuthWrapper>
                <RedeemCodeForm />
              </AuthWrapper>
            }
          />

          {/* Route cho redeem code result - yêu cầu đăng nhập */}
          <Route
            path="/redeem-code-result"
            element={
              <AuthWrapper>
                <RedeemCodeResult />
              </AuthWrapper>
            }
          />

          {/* Route cho customer info page - không yêu cầu đăng nhập */}
          <Route
            path="/thong-tin/:phone"
            element={<CustomerInfoPage />}
          />

          {/* Route cho maintenance page */}
          <Route path="/maintenance" element={<MaintenancePage />} />

          {/* Fallback route - chuyển hướng về trang kiểm tra sản phẩm */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
